import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/utils/api';
import { setAuth } from '@/utils/auth';
import { getFingerprintSafe } from '@/components/login/loginHelpers';
import useLoginMeta from '@/components/login/useLoginMeta';
import useMagicLinkPolling from '@/components/login/useMagicLinkPolling';
import LoginBrandPane from '@/components/login/LoginBrandPane';
import LanguageSelector from '@/components/login/LanguageSelector';
import OtpChallengeForm from '@/components/login/OtpChallengeForm';
import PasswordLoginForm from '@/components/login/PasswordLoginForm';
import SocialLoginButtons from '@/components/login/SocialLoginButtons';
import LoginFooter from '@/components/login/LoginFooter';

const toOtpChallenge = (d) => ({
  session_id: d.otp_session_id,
  hint: d.delivery_target_hint,
  country_label: d.country_label,
  expires_in_minutes: d.expires_in_minutes,
});

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const meta = useLoginMeta();
  const navigate = useNavigate();

  // A TOTP challenge hands off to /two-factor; otherwise store the session.
  const routeAfterAuth = (data) => {
    if (data?.requires_totp_enrollment || data?.requires_totp) {
      sessionStorage.setItem('safer_mfa_token', data.mfa_token);
      sessionStorage.setItem('safer_mfa_mode', data.requires_totp_enrollment ? 'enroll' : 'challenge');
      navigate('/two-factor');
      return;
    }
    setAuth(data.access_token, data.user || data);
    const returnUrl = sessionStorage.getItem('safer_return_url');
    sessionStorage.removeItem('safer_return_url');
    toast.success('Welcome back!');
    navigate(returnUrl || '/dashboard');
  };

  const cancelOtpChallenge = () => {
    setOtpChallenge(null);
    setOtpCode('');
  };

  useMagicLinkPolling(otpChallenge?.session_id, routeAfterAuth, () => {
    toast.error('Sign-in link expired. Please log in again.');
    cancelOtpChallenge();
  });

  const handleLogin = async ({ email, password }) => {
    setLoading(true);
    try {
      const device_fingerprint = await getFingerprintSafe();
      const { data } = await api.post('/auth/login', { email, password, device_fingerprint });
      if (data?.requires_otp) {
        setOtpChallenge(toOtpChallenge(data));
        toast.info('Verification code sent', { description: `Check ${data.delivery_target_hint} for a 6-digit code.` });
        return;
      }
      routeAfterAuth(data);
    } catch {
      // Error toast is shown by the api interceptor.
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otpChallenge?.session_id || otpCode.length !== 6) return;
    setOtpSubmitting(true);
    try {
      const device_fingerprint = await getFingerprintSafe();
      const res = await api.post('/auth/login-otp', { otp_session_id: otpChallenge.session_id, code: otpCode, device_fingerprint }, { skipErrorToast: true });
      routeAfterAuth(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code — try again');
      setOtpCode('');
    } finally {
      setOtpSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <LoginBrandPane tagline={meta.tagline} />
      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 relative">
        <LanguageSelector />
        <div className="w-full max-w-md" data-testid="login-card">
          <div className="text-center mb-8">
            <div className="lg:hidden mx-auto w-16 h-16 flex items-center justify-center mb-4" data-testid="app-logo">
              <img src="/safer-i-logo.png" alt="SAFER-i" className="w-full h-full object-contain rounded-xl" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">Welcome back</h2>
            <p className="text-base text-slate-500 mt-2" data-testid="login-tagline">Sign in to your SAFER-i™ workspace</p>
            {meta.strapline && <p className="text-xs text-slate-400 mt-1" data-testid="login-strapline">{meta.strapline}</p>}
          </div>

          {otpChallenge ? (
            <OtpChallengeForm
              challenge={otpChallenge}
              code={otpCode}
              onCodeChange={setOtpCode}
              submitting={otpSubmitting}
              onSubmit={handleOtpSubmit}
              onCancel={cancelOtpChallenge}
            />
          ) : (
            <>
              <PasswordLoginForm loading={loading} onSubmit={handleLogin} />
              <SocialLoginButtons loading={loading} setLoading={setLoading} onAuthenticated={routeAfterAuth} />
            </>
          )}

          <LoginFooter version={meta.version} date={meta.date} />
        </div>
      </div>
    </div>
  );
}
